import { ChatMessageCard } from "@/components/chat/chat-message-card";
import { useSuspenseLiveQuery } from "@/hooks/useSuspenseLiveQuery";
import { binencode } from "@/lib/binary";
import { hashBlob } from "@/lib/file";
import { formatBytes } from "@/lib/number";
import {
  hashWiredMessageAttachmentEntries,
  MessageBodyType,
  MessageDestinationType,
  MessagePacketType,
  WiredMessage,
  WiredMessageAttachment,
  WiredMessageAttachmentEntry,
  WiredMessageBody,
  WiredMessagePacket,
} from "@/moby/db";

// Type alias for a signed message with properly defined structure
// Specifically for messages of MessageBodyType.MESSAGE type
type WiredSignedMessage = {
  signature: Uint8Array;
  body: bin.Parsed<typeof WiredMessageBody> & { type: MessageBodyType.MESSAGE };
};
import { mui } from "@/mui";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { ed25519 } from "@noble/curves/ed25519";
import { SearchIcon, UploadIcon, UsersIcon, SendHorizontal, HardDriveUpload, ImageIcon } from "lucide-react";
import { getPeerAvatarColor, renderPeerAvatarEmoji, renderPeerPresenceBadge } from "@/lib/peer-profile-utils";
import React, { useEffect, useState } from 'react';
import { SelfHealingSuspense } from "@/components/self-healing-suspense";
import { toast } from "sonner";
import type bin from "typed-binary";
import { useParams, useLocation } from "wouter";

import {
  useMobyDB,
  useMobyNodeClient,
  useSession,
  useFileTransfer,
  CHUNK_SIZE_CONFIG, // Import unified chunk size system
} from "../provider";
import { useSafeFileTransferWithExtension } from "../file/shared-transfer-components";
import { type FileTransferRecord, FileTransferStateEnum } from "@/lib/file-transfer-types"; // Import for robust transfer record retry logic
import { InviteUsersToGroupDialog } from "./invite-users-to-group-dialog";
import { useLayoutPreferences } from "@/hooks/useLayoutPreferences";

export function ChatPanel() {
  const { chatAddress } = useParams<"/chats/:chatAddress">();
  const [location, navigate] = useLocation();

  const session = useSession();

  const db = useMobyDB();

  const node = useMobyNodeClient();

  const { isCompact } = useLayoutPreferences();

  const [attachments, setAttachments] = useState<File[]>([]);

  // Per-chat image rendering toggle
  const [renderImages, setRenderImages] = useState(
    () => chatAddress ? localStorage.getItem(`moby_peer_renderImages_${chatAddress}`) === 'true' : false
  );
  React.useEffect(() => {
    if (chatAddress) {
      setRenderImages(localStorage.getItem(`moby_peer_renderImages_${chatAddress}`) === 'true');
    }
  }, [chatAddress]);
  // Also listen for external changes (e.g. from edit-contact dialog)
  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.address === chatAddress) {
        setRenderImages(detail.enabled);
      }
    };
    window.addEventListener('moby-render-images-changed', handler);
    return () => window.removeEventListener('moby-render-images-changed', handler);
  }, [chatAddress]);
  const toggleRenderImages = React.useCallback(() => {
    if (!chatAddress) return;
    const next = !renderImages;
    if (next) {
      localStorage.setItem(`moby_peer_renderImages_${chatAddress}`, 'true');
    } else {
      localStorage.removeItem(`moby_peer_renderImages_${chatAddress}`);
    }
    setRenderImages(next);
    window.dispatchEvent(new CustomEvent('moby-render-images-changed', {
      detail: { address: chatAddress, enabled: next },
    }));
  }, [chatAddress, renderImages]);

  // 🚀 FIXED: Move file transfer hook to top level (React Rules of Hooks)
  // This was previously called inside .map() which caused React crash #310
  const fileTransfer = useSafeFileTransferWithExtension();

  // Auto-create chat record if needed (outside live query to avoid ReadOnlyError)
  React.useEffect(() => {
    const autoCreateChatIfNeeded = async () => {
      if (!chatAddress || !session) {
        return;
      }

      const existingChat = await db.chatByAddress(chatAddress);
      if (existingChat) {
        return; // Chat already exists
      }

      // CRITICAL: Check if this conversation is tombstoned (deleted)
      const isConversationTombstoned = await db.isConversationTombstoned(chatAddress);
      if (isConversationTombstoned) {
        console.log("[CHAT_INIT] Refusing to create chat for tombstoned conversation", {
          chatAddress: chatAddress?.slice(0, 16) + "...",
          session: session.address?.slice(0, 16) + "..."
        });
        return; // Do not create conversations that have been deleted
      }

      console.log(`[CHAT_INIT] No existing chat found for ${chatAddress}, checking if contact exists...`);
      
      // Check if this is a valid contact address
      const contact = await db.contacts.get({ address: chatAddress });
      if (contact) {
        console.log(`[CHAT_INIT] Creating new chat record for contact: ${contact.getDisplayName()} (${chatAddress})`);
        
        try {
          // Create DM chat instance automatically (same pattern as storeContact)
          await db.chats.add({
            address: chatAddress,
            type: MessageDestinationType.DM,
            peers: [chatAddress, session.address],
            nonces: {
              [chatAddress]: 0,
              [session.address]: 0
            },
            lastMessageVersion: 1,
          });
          
          console.log(`[CHAT_INIT] Successfully created chat record for ${chatAddress}`);
        } catch (error) {
          console.error(`[CHAT_INIT] Failed to create chat record for ${chatAddress}:`, error);
        }
      } else {
        console.warn(`[CHAT_INIT] No contact found for address ${chatAddress}, cannot create chat`);
      }
    };

    autoCreateChatIfNeeded();
  }, [chatAddress, session]);

  // BADGE SYSTEM: Track active chat for smart badge management
  // This prevents badge from incrementing while user is viewing the chat
  React.useEffect(() => {
    if (chatAddress) {
      // Set this chat as active - also clears its badge
      node.setActiveChatAddress(chatAddress);
    }
    
    return () => {
      // Clear active chat when leaving
      node.setActiveChatAddress(null);
    };
  }, [chatAddress, node]);

  const chat = useSuspenseLiveQuery(async () => {
    if (chatAddress === undefined) {
      return undefined;
    }

    return await db.chatByAddress(chatAddress);
  }, [chatAddress]);

  const contact = useSuspenseLiveQuery(async () => {
    if (chat?.type === MessageDestinationType.GROUP) {
      return {
        type: "group" as const,
        displayName: chat.name ?? "Group",
        initials: chat.name?.slice(0, 2) ?? "Gr",
        address: chatAddress,
      };
    }

    const peerAddress = chatAddress;
    if (peerAddress === undefined) {
      return;
    }

    const contact = await db.contacts.get({ address: peerAddress });
    if (contact === undefined) {
      return;
    }

    const displayName = contact.getDisplayName();

    return {
      type: "contact" as const,
      displayName,
      initials: contact.getInitials(),
      address: contact.address,
      profile: contact.custom?.profile, // Include remote peer profile for avatar/emoji/presence
    };
  }, [chat?.type, chat?.name, chatAddress]);

  const messages = useSuspenseLiveQuery(async () => {
    if (chat === undefined) {
      return [];
    }

    const fetchedMessages = await chat.getLatestMessages();
    
    // GROUP CHAT FIX: Sort by timestamp for chronological order
    // In star topology, messages from different participants can arrive out of order
    // due to relay timing. Version-based sorting (from getLatestMessages) doesn't
    // reflect global chronological order, so we sort by actual message timestamp.
    // This ensures messages appear in the order they were created, not relayed.
    return fetchedMessages.sort((a, b) => {
      return b.timestamp.getTime() - a.timestamp.getTime(); // Newest first (reversed)
    });
  }, [chat?.address]);
  
  // =====================================================================
  // REMOTE DELETION EVENT HANDLER - Navigate away from deleted conversations
  // =====================================================================
  useEffect(() => {
    const handleConversationDeleted = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationAddress } = customEvent.detail;
      
      console.log("[CHAT-PANEL-DELETE] Received moby-conversation-deleted event", {
        deletedAddress: conversationAddress?.slice(0, 16) + "...",
        currentChatAddress: chatAddress?.slice(0, 16) + "...",
        isCurrentChat: chatAddress === conversationAddress
      });
      
      // Navigate away if currently viewing the deleted conversation
      if (chatAddress && chatAddress === conversationAddress) {
        console.log("[CHAT-PANEL-DELETE] Navigating away from deleted conversation", {
          deletedChatAddress: conversationAddress?.slice(0, 16) + "...",
          currentPath: location
        });
        
        // Navigate to the main chats view
        navigate('/chats');
        
        // Show a toast notification to inform the user
        toast.info("Conversation was deleted", {
          description: "You have been redirected to the main chat view."
        });
      }
    };
    
    window.addEventListener('moby-conversation-deleted', handleConversationDeleted);
    
    return () => {
      window.removeEventListener('moby-conversation-deleted', handleConversationDeleted);
    };
  }, [chatAddress, location, navigate]);

  // =====================================================================
  // FILE PREPARATION TOAST — replaces inline upload card with toast pattern
  // Listens for moby:transfer-progress events dispatched by provider during
  // Merkle tree generation (0-50%) and OPFS write (50-100%)
  // =====================================================================
  useEffect(() => {
    const handleTransferProgress = (event: Event) => {
      const { sessionId, progress, phase, fileName } = (event as CustomEvent).detail;
      if (!sessionId || !fileName) return;

      switch (phase) {
        case 'preparing':
          toast.loading(`Preparing ${fileName}...`, { id: sessionId });
          break;
        case 'merkle':
          toast.loading(`Building merkle tree... ${Math.round(progress)}%`, { id: sessionId });
          break;
        case 'opfs':
          toast.loading(`Writing to storage... ${Math.round(progress)}%`, { id: sessionId });
          break;
        case 'group-broadcasting':
          toast.success(`${fileName} sent to ${(event as CustomEvent).detail.memberCount} members`, { id: sessionId });
          break;
        case 'complete':
          toast.success(`${fileName} ready for transfer`, { id: sessionId });
          break;
      }
    };

    window.addEventListener('moby:transfer-progress', handleTransferProgress);
    return () => {
      window.removeEventListener('moby:transfer-progress', handleTransferProgress);
    };
  }, []);

  // Helper function to create chat messages with file attachments
  const createChatMessageWithAttachments = async (text: string, fileTransferData: Array<{hash: string, sessionId: string, file: File}>) => {
    console.log(`[ATTACHMENT_CREATE] Starting chat message creation with ${fileTransferData.length} attachments:`, fileTransferData.map(d => ({ hash: d.hash, sessionId: d.sessionId.slice(0, 8), fileName: d.file.name })));
    
    if (!contact || !session) {
      console.warn('[UNIFIED] Cannot create chat message: missing contact or session');
      return;
    }
    
    try {
      // Get nonce and version from chat context if available, otherwise use defaults
      const messageNonce = chat?.getNextNonce(session.address) ?? 1;
      const messageVersion = chat?.getNextVersion() ?? 1;

      // Create proper WiredMessageAttachment objects from file transfer data
      const attachments: bin.Parsed<typeof WiredMessageAttachment>[] = await Promise.all(
        fileTransferData.map(async ({hash, sessionId, file}) => {
          const fileHash = hexToBytes(hash);
          
          // Use file hash as root for proper database and transfer lookup
          // The attachment card needs to find transfers by root hash
          const attachmentEntry: bin.Parsed<typeof WiredMessageAttachmentEntry> = {
            root: fileHash, // Use file hash as root (matches transfer.rootHash)
            path: file.name, // Original filename - this should be the full name
            size: BigInt(file.size), // File size as BigInt
            chunkSize: CHUNK_SIZE_CONFIG.DEFAULT, // Use unified chunk size system
          };
          
          return {
            hash: fileHash, // Keep file hash for attachment identification
            entries: [attachmentEntry],
          };
        })
      );

      // Create a properly typed message body with file attachments
      const messageBody: bin.Parsed<typeof WiredMessageBody> = {
        type: MessageBodyType.MESSAGE,
        text: text,
        attachments: attachments, // Proper WiredMessageAttachment objects
        from: {
          address: hexToBytes(session.address),
          nonce: messageNonce,
        },
        to: {
          type:
            contact.type === "contact"
              ? MessageDestinationType.DM
              : MessageDestinationType.GROUP,
          address: hexToBytes(contact.address),
        },
        version: messageVersion,
        timestamp: BigInt(Date.now()),
        groupInvite: undefined,
      };

      // Sign the message
      const signature = ed25519.sign(
        binencode(WiredMessageBody, messageBody),
        hexToBytes(session.secret),
      );

      // Create the full signed message with proper type structure
      const message: WiredSignedMessage = {
        signature,
        body: messageBody as bin.Parsed<typeof WiredMessageBody> & { type: MessageBodyType.MESSAGE },
      };

      // Handle direct message vs. group message
      switch (contact.type) {
        case "contact": {
          const peer = node.getOrCreatePeer(contact.address);
          
          // Check if connection state is viable for sending messages
          if (peer.connection.connectionState === "connected") {
            console.log("[UNIFIED] Sending direct message with file attachments", { peer: peer.address, attachments: attachments.length });

            // Send the message via the chat channel
            void peer.chat.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.MESSAGE,
                message,
              }),
            );
          } else {
            console.log("[UNIFIED] Peer not connected, message with attachments will be stored for later delivery");
          }

          // Store the message in the database
          await db.storeMessage(session.address, message);
          break;
        }
        
        case "group": {
          if (chat === undefined) {
            throw new Error("Group chat is undefined");
          }

          // Send to all connected peers in the group
          for (const peerAddress of chat.peers) {
            const peer = node.getOrCreatePeer(peerAddress);
            if (peer.connection.connectionState !== "connected") {
              continue;
            }

            console.log("[UNIFIED] Sending group message with file attachments to peer", { peer: peer.address, attachments: attachments.length });
            
            void peer.chat.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.MESSAGE,
                message,
              }),
            );
          }

          // Store the message in the database
          await db.storeMessage(session.address, message);
          console.log(`[ATTACHMENT_CREATE] Message stored in database successfully`, {
            messageId: bytesToHex(message.signature),
            attachmentCount: attachments.length,
            messageText: text
          });
          break;
        }
      }
      
      console.log(`[UNIFIED] Created chat message with ${attachments.length} file attachments`);
      
      // FIX 2c: Mark sender's db.files entries as downloaded=1
      // The sender already has the file locally. Without this, after the transfer
      // completes and is cleaned from in-memory maps, the AttachmentCard shows
      // "Connecting..." permanently because file.downloaded === 0 ≠ likelySender.
      for (const data of fileTransferData) {
        try {
          const fileEntry = await db.files.get({ root: data.hash });
          if (fileEntry && fileEntry.downloaded !== 1) {
            await db.files.update(fileEntry, (f) => { f.downloaded = 1; });
          }
        } catch (e) {
          // Non-critical — card will still work via transfer lookup
          console.debug('[FIX_2c] Failed to mark sender file as downloaded:', e);
        }
      }
      
    } catch (error) {
      console.error('[UNIFIED] Failed to create chat message with attachments:', error);
      toast.error('Failed to create chat message with file attachments');
    }
  };
  
  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (contact === undefined || session === undefined) {
      return;
    }

    if (contact.type === "group" && chat === undefined) {
      toast.warning(
        "Unable to send message into a group chat that doesn't exist.",
      );
      return;
    }

    const form = e.currentTarget;
    const formData = new FormData(form);

    const text = formData.get("text")?.toString().trim() ?? "";

    const files = formData
      .getAll("attachments")
      .filter(
        (file): file is File =>
          file instanceof File && file.name !== "" && file.size > 0,
      );

    if (text === "" && files.length === 0) {
      return;
    }

    // Files are now sent immediately on selection through the unified provider context
    // Chat messages with file attachments are also created immediately on selection
    // No need to send them again on form submission to avoid race conditions
    if (files.length > 0) {
      console.log(`[UNIFIED] Files already sent on selection - skipping duplicate send on form submission`);
      console.log(`[UNIFIED] ${files.length} files are being handled by provider context with chat message attachment linking`);
    }

    // If we have no text, don't send a text message
    if (text === "") {
      // Clear the form
      form.reset();
      setAttachments([]);
      return;
    }

    try {
      // Get nonce and version from chat context if available, otherwise use defaults
      const messageNonce = chat?.getNextNonce(session.address) ?? 1;
      const messageVersion = chat?.getNextVersion() ?? 1;

      // Create a properly typed message body based on backup structure
      const messageBody: bin.Parsed<typeof WiredMessageBody> = {
        type: MessageBodyType.MESSAGE,
        text: text,
        attachments: [], // Start with empty attachments for basic text messages
        from: {
          address: hexToBytes(session.address),
          nonce: messageNonce,
        },
        to: {
          type:
            contact.type === "contact"
              ? MessageDestinationType.DM
              : MessageDestinationType.GROUP,
          address: hexToBytes(contact.address),
        },
        version: messageVersion,
        timestamp: BigInt(Date.now()),
        groupInvite: undefined,
      };

      // Sign the message
      const signature = ed25519.sign(
        binencode(WiredMessageBody, messageBody),
        hexToBytes(session.secret),
      );

      // Create the full signed message with proper type structure
      const message: WiredSignedMessage = {
        signature,
        body: messageBody as bin.Parsed<typeof WiredMessageBody> & { type: MessageBodyType.MESSAGE },
      };

      // Handle direct message vs. group message
      switch (contact.type) {
        case "contact": {
          const peer = node.getOrCreatePeer(contact.address);

          // P0-H: Send unconditionally. MobyChannel.send() (src/moby/channel.ts:170-176)
          // has an internal wait-for-open loop that queues the message until the next
          // channel-open event, so blipping/reconnecting peers will receive the message
          // on recovery. Previously this path was gated on connectionState === "connected",
          // which silently dropped every chat message sent during a WebRTC blip.
          console.log("Sending direct message", {
            peer: peer.address,
            connectionState: peer.connection.connectionState,
            channelReady: peer.chat.channel?.readyState === "open",
          });

          void peer.chat.send(
            binencode(WiredMessagePacket, {
              type: MessagePacketType.MESSAGE,
              message,
            }),
          );

          // Store the message in the database
          await db.storeMessage(session.address, message);
          
          // Increment outbound unsynced count for badge display
          await db.incrementOutboundUnsyncedCount(contact.address);
          break;
        }
        
        case "group": {
          if (chat === undefined) {
            throw new Error("Group chat is undefined");
          }

          // P0-H: Send to ALL peers in the group. MobyChannel.send() queues until
          // the channel is open, so blipping/reconnecting peers will receive the
          // message on recovery. Previously this loop skipped any peer not currently
          // in connectionState === "connected", silently dropping group chat during
          // every WebRTC blip.
          for (const peerAddress of chat.peers) {
            const peer = node.getOrCreatePeer(peerAddress);

            console.log("Sending group message to peer", {
              peer: peer.address,
              connectionState: peer.connection.connectionState,
              channelReady: peer.chat.channel?.readyState === "open",
            });

            void peer.chat.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.MESSAGE,
                message,
              }),
            );
          }

          // Store the message in the database
          await db.storeMessage(session.address, message);
          
          // Increment outbound unsynced count for badge display (group)
          await db.incrementOutboundUnsyncedCount(chat.address);
          break;
        }
      }
      
      // Reset form and attachments on success
      form.reset();
      setAttachments([]);
      
    } catch (error) {
      toast.error("Failed to send chat message");
      console.error(error);
    }
  };

  useEffect(() => {
    if (chat === undefined) {
      return;
    }

    let unsubscribe: (() => void) | undefined;
    
    if (chat.type === MessageDestinationType.GROUP) {
      // EVENT-DRIVEN GROUP SYNC: Register + one-time catch-up on panel open
      node.subscribeGroupsToEventSync([chat.address]).then((unsub) => {
        unsubscribe = unsub;
      }).catch((error) => {
        console.error('Failed to register group for event sync:', error);
      });
      // One-time sync to catch up on any missed messages
      void node.syncGroupOnce(chat.address);
    } else {
      // DM chats use polling-based sync via subscribeChatsToSync
      node.subscribeChatsToSync([chat.address]).then((unsub) => {
        unsubscribe = unsub;
      }).catch((error) => {
        console.error('Failed to subscribe chat to sync:', error);
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [chat?.address]);

  if (session === undefined || contact === undefined) {
    return (
      <div className={`bg-surface rounded-t-large sm:rounded-tr-large relative flex flex-1 flex-col items-center justify-center overflow-x-hidden rounded-tr-none ${isCompact ? '' : 'sm:mr-6'}`}>
        <div className="text-center space-y-4 max-w-md px-8">
          <div className="bg-outline/12 rounded-full p-6 mx-auto w-24 h-24 flex items-center justify-center">
            <svg className="w-12 h-12 text-outline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div>
            <h3 className="text-title-lg text-on-surface mb-2">Select a conversation</h3>
            <p className="text-body-md text-on-surface-variant">
              Choose a conversation from the list to start chatting, or add a new contact to begin messaging.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-surface rounded-t-large sm:rounded-tr-large relative flex flex-1 flex-col overflow-x-hidden rounded-tr-none ${isCompact ? '' : 'sm:mr-6'}`}
    >
      <div
        className={`bg-surface-container-highest rounded-t-large sm:rounded-tr-large flex min-w-0 shrink-0 items-center justify-between gap-4 rounded-tr-none p-4`}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative size-9 shrink-0">
            <div 
              className="grid size-9 place-items-center rounded-full text-white"
              style={{ backgroundColor: getPeerAvatarColor((contact as any).profile) }}
            >
              <div className="text-label-md">{contact.initials}</div>
              {renderPeerAvatarEmoji((contact as any).profile, "sm")}
            </div>
            {renderPeerPresenceBadge((contact as any).profile, "sm")}
          </div>
          <div className="truncate">
            <h2 className="text-title-lg -mb-1 block truncate">
              {contact.displayName}
            </h2>
            <code className="bg-surface-container text-on-surface rounded-extra-small truncate px-1 py-0.5 text-xs">
              {contact.address}
            </code>
          </div>
        </div>
        <div className="shrink-0 space-x-2 flex items-center">
          <button
            onClick={toggleRenderImages}
            className={`group relative shrink-0 self-end rounded-full p-2 transition duration-150 hover:cursor-pointer ${
              renderImages ? 'text-primary bg-primary/10' : 'text-on-surface-variant'
            }`}
            title={renderImages ? 'Render images: ON — click to disable' : 'Render images: OFF — click to enable'}
            aria-label="Toggle image rendering"
          >
            <ImageIcon size={24} />
            <span
              aria-hidden
              className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
            />
          </button>

          <button
            disabled
            className={`group text-on-surface-variant relative shrink-0 self-end rounded-full p-2 transition duration-150 hover:cursor-pointer disabled:pointer-events-none disabled:opacity-38`}
            title="Search"
            aria-label="Search"
          >
            <SearchIcon size={24} />
            <span
              aria-hidden
              className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
            />
          </button>

          {chat?.type === MessageDestinationType.GROUP && (
            <InviteUsersToGroupDialog groupAddress={chat.address}>
              <mui.Dialog.Trigger className="text-on-surface-variant relative shrink-0 self-end rounded-full p-2 transition duration-150 hover:cursor-pointer disabled:pointer-events-none disabled:opacity-38">
                <UsersIcon size={24} />
              </mui.Dialog.Trigger>
            </InviteUsersToGroupDialog>
          )}
        </div>
      </div>

      {/* Messages - Material Cards */}
      <div className="bg-surface flex flex-1 basis-0 flex-col-reverse gap-4 overflow-y-auto p-6">
        {messages.map((message) => (
          <SelfHealingSuspense
            key={message.signature}
            name="chat-message"
            watchdogMs={5000}
            maxRemounts={1}
            fallback={
              <div className="flex justify-start animate-pulse">
                <div className="mr-2 size-9 shrink-0 rounded-full bg-surface-container-highest/40" />
                <div className="max-w-[70%] space-y-1">
                  <div className="h-10 w-48 rounded-medium bg-surface-container-highest/30" />
                  <div className="h-3 w-24 rounded bg-surface-container-highest/20" />
                </div>
              </div>
            }
          >
            <ChatMessageCard
              messageSignature={message.signature}
            />
          </SelfHealingSuspense>
        ))}
      </div>

      {/* Message Input - Material TextField */}
      <div className="shrink-0 p-4">
        {/* File preparation progress is now shown via toast notifications
           (moby:transfer-progress event listener above) instead of inline cards */}
        
        {/* Receiver: Show incoming file preparation notification */}
        {(() => {
          // FIX: Check BOTH queuedTransfers.receive AND activeTransfers for PREPARING placeholders
          // Backend adds airplane protocol placeholders to receiveQueue (provider.tsx:8537)
          // Context exposes it as queuedTransfers.receive (see FileTransferContextType interface)
          // Then moves to activeTransfers when FILE_METADATA arrives (if capacity available)
          const allIncomingTransfers = [
            ...Array.from(fileTransfer?.queuedTransfers?.receive?.values() || []),
            ...Array.from(fileTransfer?.activeTransfers.values() || [])
          ];
          
          const incomingPreparingTransfers = allIncomingTransfers
            .filter((record: any) => 
              record.state === FileTransferStateEnum.PREPARING &&
              record.metadata?.direction === 'inbound' &&
              record.metadata?.peerAddress === contact?.address  // Check sender's address from metadata
            );
          
          return incomingPreparingTransfers.length > 0 && (
            <div className="mb-2 flex gap-2 overflow-x-auto">
              <div className="bg-surface-container-highest text-on-surface rounded-medium shrink-0 p-3 flex items-center gap-3 animate-pulse">
                <SendHorizontal className="text-primary" size={24} />
                <div>
                  <div className="text-title-sm text-on-primary-container">
                    {contact?.displayName || 'Contact'} is preparing a file...
                  </div>
                  <div className="text-label-sm text-on-surface-variant">
                    This may take a few minutes for large files
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        <form
          onSubmit={handleSendMessage}
          className="flex items-center gap-2"
        >
          {/* File picker - individual files */}
          <input
            type="file"
            name="attachments"
            id="attachments"
            hidden
            multiple
            onChange={async (e) => {
              const files = e.target.files;
              if (files === null) {
                return;
              }

              const attachments = Array.from(files).filter(
                (file): file is File =>
                  file instanceof File && file.name !== "",
              );

              setAttachments(attachments);
                            // Send files immediately through unified provider context and capture attachment hashes
                if (attachments.length > 0 && contact && fileTransfer) {
                  console.log(`[UNIFIED] Starting immediate file transfers for ${attachments.length} files through provider context`);
                  
                  const fileTransferData: Array<{hash: string, sessionId: string, file: File}> = [];
                  
                  for (const file of attachments) {
                    try {
                      console.log(`[UNIFIED] Sending file ${file.name} through unified provider context`);
                      
                      // =====================================================================
                      // EXTENSION INTEGRATION POINT 2: Sender Side Capability Detection
                      // =====================================================================
                      // Detect extension capabilities before initiating transfer
                      let senderCapabilities = null;
                      let recommendedPath: 'opfs' | 'extension' = 'opfs';
                      
                      try {
                        // Import extension service dynamically to avoid loading overhead
                        const { extensionCapabilityService } = await import('@/services/extension-capability-service');
                        const capabilities = await extensionCapabilityService.detectExtension();
                        
                        if (capabilities.available && file.size > 100 * 1024 * 1024) { // > 100MB (conservative for testing)
                          senderCapabilities = capabilities.capabilities;
                          recommendedPath = 'extension';
                          console.log('[EXTENSION_INTEGRATION] Sender: Large file detected, recommending extension path', {
                            fileName: file.name,
                            fileSize: (file.size / 1024 / 1024).toFixed(1) + 'MB',
                            extensionVersion: capabilities.version
                          });
                        }
                      } catch (error) {
                        console.log('[EXTENSION_INTEGRATION] Sender: Extension not available, using OPFS path', {
                          fileName: file.name,
                          error: error instanceof Error ? error.message : String(error)
                        });
                      }
                      
                      // TIMING FIX: Calculate file hash synchronously before initiating transfer
                      // For large files, use streaming hash calculation
                      let rootHash: string;
                      
                      // Debug file size to understand the NotReadableError
                      console.log(`[FILE_DEBUG] File details before hash:`, {
                        name: file.name,
                        size: file.size,
                        sizeGB: (file.size / (1024 * 1024 * 1024)).toFixed(2),
                        sizeMB: (file.size / (1024 * 1024)).toFixed(2),
                        type: file.type,
                        lastModified: file.lastModified,
                        isLargeFile: file.size > 400 * 1024 * 1024
                      });
                      
                      // Test if we can still read the file
                      try {
                        // Try to read just 1 byte to test access
                        const testSlice = file.slice(0, 1);
                        const testBuffer = await testSlice.arrayBuffer();
                        console.log(`[FILE_DEBUG] File access test passed, can read file`);
                      } catch (testError) {
                        console.error(`[FILE_DEBUG] File access test FAILED:`, testError);
                        throw new Error(`File is no longer readable: ${testError}`);
                      }
                      
                      if (file.size > 400 * 1024 * 1024) { // Files > 400MB
                        // Stream-based hash calculation for large files
                        console.log(`[UNIFIED] Using streaming hash for large file: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
                        
                        const hashBuffer = await crypto.subtle.digest('SHA-256', new Uint8Array(0)); // Start with empty
                        const tempHash = new Uint8Array(hashBuffer);
                        
                        // Use file metadata as temporary hash for large files
                        // The actual hash will be calculated during chunking in provider
                        const metadataString = `${file.name}-${file.size}-${file.lastModified}`;
                        const metadataBuffer = new TextEncoder().encode(metadataString);
                        const metadataHash = await crypto.subtle.digest('SHA-256', metadataBuffer);
                        rootHash = bytesToHex(new Uint8Array(metadataHash));
                        
                        console.log(`[UNIFIED] Using metadata-based hash for large file:`, {
                          fileName: file.name,
                          fileSize: file.size,
                          tempHash: rootHash.slice(0, 8)
                        });
                      } else {
                        // For smaller files, use direct hash calculation
                        try {
                          const fileBuffer = await file.arrayBuffer();
                          const fileHash = await crypto.subtle.digest('SHA-256', fileBuffer);
                          rootHash = bytesToHex(new Uint8Array(fileHash));
                        } catch (hashError) {
                          console.error(`[FILE_DEBUG] Hash calculation failed for ${file.name}:`, hashError);
                          // Fall back to metadata hash if direct hash fails
                          const metadataString = `${file.name}-${file.size}-${file.lastModified}`;
                          const metadataBuffer = new TextEncoder().encode(metadataString);
                          const metadataHash = await crypto.subtle.digest('SHA-256', metadataBuffer);
                          rootHash = bytesToHex(new Uint8Array(metadataHash));
                          console.log(`[FILE_DEBUG] Using fallback metadata hash due to read error`);
                        }
                      }
                      
                      // CRITICAL: For groups, use any group peer to initiate transfer
                      // Don't create peer connection to group address itself
                      // For P2P, use the contact's address directly
                      const sessionId = crypto.randomUUID();
                      
                      if (contact.type === 'group') {
                        // Group transfer: use first available peer from group to initiate
                        if (!chat || !chat.peers || chat.peers.length === 0) {
                          console.error(`[FILE_DEBUG] Cannot send to group: no peers available`);
                          toast.error(`Transfer failed: ${file.name}`, {
                            description: 'No group members available'
                          });
                          continue;
                        }
                        
                        // Use first peer in group to call sendFile (it will detect group and route correctly)
                        const groupPeer = node.getOrCreatePeer(chat.peers[0]);
                        console.log(`[FILE_DEBUG] Group transfer: ${file.name} via peer ${chat.peers[0].slice(0, 8)} to group ${contact.address.slice(0, 8)}`);
                        
                        toast.loading(`Preparing ${file.name}...`, { id: sessionId });
                        void groupPeer.sendFile(file, contact.address, sessionId, rootHash).catch((error: Error) => {
                          console.error(`[UNIFIED] Group transfer error for ${file.name}:`, error);
                          toast.error(`Transfer failed: ${file.name}`, {
                            id: sessionId,
                            description: error instanceof Error ? error.message : String(error)
                          });
                        });
                      } else {
                        // P2P transfer: use contact address directly
                        console.log(`[FILE_DEBUG] P2P transfer: ${file.name} to peer ${contact.address.slice(0, 8)}`);
                        const peer = node.getOrCreatePeer(contact.address);
                        
                        toast.loading(`Preparing ${file.name}...`, { id: sessionId });
                        void peer.sendFile(file, contact.address, sessionId, rootHash).catch((error: Error) => {
                          console.error(`[UNIFIED] P2P transfer error for ${file.name}:`, error);
                          toast.error(`Transfer failed: ${file.name}`, {
                            id: sessionId,
                            description: error instanceof Error ? error.message : String(error)
                          });
                        });
                      }
                      
                      // Log extension integration metadata for debugging
                      console.log('[EXTENSION_INTEGRATION] Sender metadata:', {
                        sessionId: sessionId.slice(0, 8),
                        fileName: file.name,
                        senderCapabilities: !!senderCapabilities,
                        recommendedPath
                      });
                      console.log(`[UNIFIED] File transfer initiated with session ID: ${sessionId}`);
                      
                      // SYNCHRONOUS FIX: Use calculated hash instead of waiting for transfer records
                      fileTransferData.push({
                        hash: rootHash,
                        sessionId,
                        file
                      });
                      
                      console.log(`[ATTACHMENT_FIX] Synchronously captured file data:`, {
                        sessionId: sessionId.slice(0, 8),
                        rootHash: rootHash.slice(0, 8),
                        fileName: file.name,
                        fileSize: file.size
                      });
                      
                      // Toast lifecycle managed by moby:transfer-progress event listener
                    } catch (error) {
                      console.error(`[UNIFIED] Error sending file: ${error}`);
                      toast.error(`Failed to send file ${file.name}`, { 
                        description: error instanceof Error ? error.message : String(error) 
                      });
                    }
                  }
                  
                  // CRITICAL FIX: Create chat message with file attachments if any files were successfully sent
                  if (fileTransferData.length > 0) {
                    console.log(`[ATTACHMENT_FIX] Creating chat message with ${fileTransferData.length} attachments`);
                    
                    // Create message with actual filename(s) and size(s)
                    const fileDescriptions = fileTransferData.map(data => {
                      const sizeStr = formatBytes(data.file.size);
                      return `${data.file.name} (${sizeStr})`;
                    });
                    
                    const messageText = fileTransferData.length === 1 
                      ? `Transfer ${fileDescriptions[0]}`
                      : `Transfer ${fileTransferData.length} files: ${fileDescriptions.join(', ')}`;
                    
                    await createChatMessageWithAttachments(messageText, fileTransferData);
                    
                    // Clear attachments after chat message created — toast handles visual feedback
                    setAttachments([]);
                  } else {
                    console.warn(`[ATTACHMENT_FIX] No transfer data available - chat message with attachments will not be created`);
                  }
              } else if (!contact) {
                console.warn(`[UNIFIED] No contact available for file transfer`);
                toast.warning(`Please select a contact first`);
              } else if (!fileTransfer) {
                console.warn(`[UNIFIED] FileTransfer provider not available`);
                toast.warning(`File transfer service unavailable`);
              }
            }}
          />
          
          {/* Directory picker - entire folder */}
          <input
            type="file"
            name="directory"
            id="directory-picker"
            hidden
            multiple
            {...({ webkitdirectory: 'true' } as any)}
            onChange={async (e) => {
              const files = e.target.files;
              if (files === null) {
                return;
              }

              const directoryFiles = Array.from(files).filter(
                (file): file is File =>
                  file instanceof File && file.name !== "",
              );

              // Don't set attachments yet - will set after TAR creation
              
              // NEW: Compress directory to TAR before transfer
              if (directoryFiles.length > 0 && contact && fileTransfer) {
                console.log(`[TAR][DIRECTORY] Compressing ${directoryFiles.length} files to TAR archive`);
                
                // Show progress toast BEFORE try block so it's accessible in catch for dismissal
                const toastId = toast.loading(`Creating TAR archive from ${directoryFiles.length} files...`);
                
                try {
                  // Import TAR utility
                  const { createTarArchive, getDirectoryName } = await import('@/lib/tar-utils');
                  
                  // Extract directory name for TAR filename (needed for progress message)
                  const dirName = getDirectoryName(directoryFiles);
                  const tarFilename = `${dirName}.tar`;
                  
                  // Create TAR archive with streaming to OPFS (handles 6GB+ directories)
                  // Returns { file, opfsPath } for rename optimization (avoids double-copy)
                  const tarResult = await createTarArchive(directoryFiles, (progress) => {
                    toast.loading(`Creating TAR: ${progress.processedFiles}/${progress.totalFiles} files (${(progress.processedBytes / 1024 / 1024).toFixed(1)} MB)`, { id: toastId });
                  });
                  
                  // Rename the streamed file with proper directory name
                  // Attach opfsPath metadata so provider can rename instead of copying again
                  const tarFile = new File([tarResult.file as Blob], tarFilename, { type: 'application/x-tar' });
                  (tarFile as any).__opfsPath = tarResult.opfsPath;
                  
                  console.log(`[TAR][DIRECTORY] TAR archive created: ${tarFilename} (${(tarFile.size / 1024 / 1024).toFixed(2)} MB)`);
                  toast.success(`TAR archive created: ${tarFilename}`, { id: toastId });
                  
                  // FIX: Set attachments to only the TAR file (not all directory files)
                  setAttachments([tarFile]);
                  
                  // Send the single TAR file through existing transfer system
                  console.log(`[TAR][DIRECTORY] Starting transfer of TAR archive through provider context`);
                  
                  const fileTransferData: Array<{hash: string, sessionId: string, file: File}> = [];
                  
                  const file = tarFile;
                  {
                  try {
                    console.log(`[UNIFIED][DIRECTORY] Sending file ${file.name} through unified provider context`);
                    
                    // =====================================================================
                    // EXTENSION INTEGRATION POINT 2: Sender Side Capability Detection (const peer = node.peers.get is deprecated)
                    // =====================================================================
                    // Detect extension capabilities before initiating transfer
                    let senderCapabilities = null;
                    let recommendedPath: 'opfs' | 'extension' = 'opfs';
                    
                    try {
                      // Import extension service dynamically to avoid loading overhead
                      const { extensionCapabilityService } = await import('@/services/extension-capability-service');
                      const capabilities = await extensionCapabilityService.detectExtension();
                      
                      if (capabilities.available && file.size > 100 * 1024 * 1024) { // > 100MB
                        senderCapabilities = capabilities.capabilities;
                        recommendedPath = 'extension';
                        console.log('[EXTENSION_INTEGRATION][DIRECTORY] Large file detected, recommending extension path', {
                          fileName: file.name,
                          fileSize: (file.size / 1024 / 1024).toFixed(1) + 'MB',
                          extensionVersion: capabilities.version
                        });
                      }
                    } catch (error) {
                      console.log('[EXTENSION_INTEGRATION][DIRECTORY] Extension not available, using OPFS path', {
                        fileName: file.name,
                        error: error instanceof Error ? error.message : String(error)
                      });
                    }
                    
                    // Hash calculation for large files
                    let rootHash: string;
                    if (file.size > 400 * 1024 * 1024) { // Files > 400MB
                      // Use file metadata as temporary hash for large files
                      const metadataString = `${file.name}-${file.size}-${file.lastModified}`;
                      const metadataBuffer = new TextEncoder().encode(metadataString);
                      const metadataHash = await crypto.subtle.digest('SHA-256', metadataBuffer);
                      rootHash = bytesToHex(new Uint8Array(metadataHash));
                      
                      console.log(`[HASH_CALC][DIRECTORY] Using metadata-based hash for large file:`, {
                        fileName: file.name,
                        fileSize: file.size,
                        hash: rootHash.slice(0, 8)
                      });
                    } else {
                      const hashResult = await hashBlob(file);
                      rootHash = bytesToHex(hashResult);
                      console.log(`[HASH_CALC][DIRECTORY] Using direct hash for small file:`, {
                        fileName: file.name,
                        fileSize: file.size,
                        hash: rootHash.slice(0, 8)
                      });
                    }
                    
                    // CS-3 FIX: Pre-generate sessionId and pass rootHash as attachmentHash.
                    // Mirror the regular file picker: branch on contact.type and call
                    // peer.sendFile directly (bypasses the context wrapper which has no
                    // sessionId/attachmentHash params), so ATTACHMENT_HASH_BRIDGE is set.
                    const sessionId = crypto.randomUUID();
                    toast.loading(`Preparing ${tarFilename} for transfer...`, { id: sessionId });
                    
                    if (contact.type === 'group') {
                      if (!chat || !chat.peers || chat.peers.length === 0) {
                        throw new Error('No group members available');
                      }
                      const groupPeer = node.getOrCreatePeer(chat.peers[0]);
                      void groupPeer.sendFile(file, contact.address, sessionId, rootHash).catch((error: Error) => {
                        console.error(`[UNIFIED][DIRECTORY] Group transfer error for ${file.name}:`, error);
                        toast.error(`Transfer failed: ${file.name}`, {
                          id: sessionId,
                          description: error instanceof Error ? error.message : String(error)
                        });
                      });
                    } else {
                      const peer = node.getOrCreatePeer(contact.address);
                      if (peer.connection.connectionState !== 'connected') {
                        throw new Error('Peer not connected');
                      }
                      void peer.sendFile(file, contact.address, sessionId, rootHash).catch((error: Error) => {
                        console.error(`[UNIFIED][DIRECTORY] Transfer error for ${file.name}:`, error);
                        toast.error(`Transfer failed: ${file.name}`, {
                          id: sessionId,
                          description: error instanceof Error ? error.message : String(error)
                        });
                      });
                    }
                    
                    console.log(`[UNIFIED][DIRECTORY] File transfer initiated for ${file.name}:`, {
                      sessionId: sessionId.slice(0, 8),
                      rootHash: rootHash.slice(0, 8),
                      fileSize: file.size,
                      recommendedPath
                    });
                    
                    // SYNCHRONOUS FIX: Use calculated hash instead of waiting for transfer records
                    fileTransferData.push({
                      hash: rootHash,
                      sessionId,
                      file
                    });
                    
                    console.log(`[ATTACHMENT_FIX][DIRECTORY] Synchronously captured file data:`, {
                      sessionId: sessionId.slice(0, 8),
                      rootHash: rootHash.slice(0, 8),
                      fileName: file.name,
                      fileSize: file.size
                    });
                    
                  } catch (error) {
                    console.error(`[UNIFIED][DIRECTORY] Error sending file: ${error}`);
                    toast.error(`Failed to send file ${file.name}`, { 
                      description: error instanceof Error ? error.message : String(error) 
                    });
                  }
                }
                
                // CRITICAL FIX: Create chat message with TAR file attachment
                if (fileTransferData.length > 0) {
                  console.log(`[TAR][DIRECTORY] Creating chat message with TAR archive attachment (${directoryFiles.length} files compressed)`);
                  
                  // Create message with TAR filename and original file count
                  const tarData = fileTransferData[0];
                  const sizeStr = formatBytes(tarData.file.size);
                  const messageText = `Transfer ${tarData.file.name} (${sizeStr}) - ${directoryFiles.length} files compressed`;
                  
                  await createChatMessageWithAttachments(messageText, fileTransferData);
                  
                  // FIX: Clear upload indicator after chat message created
                  // Chat attachment card will now show transfer progress
                  // This prevents duplicate UI (upload indicator + attachment card)
                  setAttachments([]);
                  
                  toast.success(`Started TAR transfer`, {
                    description: `${directoryFiles.length} files compressed to ${tarFilename}`
                  });
                } else {
                  console.warn(`[ATTACHMENT_FIX][DIRECTORY] No transfer data available - chat message with attachments will not be created`);
                }
                
                } catch (tarError) {
                  console.error(`[TAR][DIRECTORY] Error creating TAR archive:`, tarError);
                  // CRITICAL FIX: Dismiss the loading toast by using the same toastId
                  toast.error(`Failed to create TAR archive`, {
                    id: toastId,
                    description: tarError instanceof Error ? tarError.message : String(tarError)
                  });
                }
              } else if (!contact) {
                console.warn(`[UNIFIED][DIRECTORY] No contact available for file transfer`);
                toast.warning(`Please select a contact first`);
              } else if (!fileTransfer) {
                console.warn(`[UNIFIED][DIRECTORY] FileTransfer provider not available`);
                toast.warning(`File transfer service unavailable`);
              }
            }}
          />
          
          {/* Upload Files Button */}
          <label
            htmlFor="attachments"
            className={`group text-on-surface-variant relative rounded-full p-2 transition duration-150 hover:cursor-pointer`}
            title="Upload files"
          >
            <UploadIcon size={20} />
            <span
              aria-hidden
              className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
            />
          </label>
          
          {/* Upload Directory Button */}
          <label
            htmlFor="directory-picker"
            className={`group text-on-surface-variant relative rounded-full p-2 transition duration-150 hover:cursor-pointer`}
            title="Upload directory"
          >
            <HardDriveUpload size={20} />
            <span
              aria-hidden
              className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
            />
          </label>
          <div
            className={`bg-surface-container-highest flex flex-1 items-center rounded-full px-4 py-2`}
          >
            <input
              type="text"
              id="text"
              name="text"
              placeholder="Type a message..."
              className={`text-on-surface placeholder:text-on-surface-variant flex-1 bg-transparent outline-none`}
            />
            <div className="flex gap-2">
              {/* <button
                type="button"
                disabled
                className={`group text-on-surface-variant relative rounded-full p-1 transition duration-150 hover:cursor-pointer disabled:pointer-events-none disabled:opacity-38`}
              >
                <SmileIcon size={20} />
                <span
                  aria-hidden
                  className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
                />
              </button>
              <button
                type="button"
                disabled
                className={`group text-on-surface-variant relative rounded-full p-1 transition duration-150 hover:cursor-pointer disabled:pointer-events-none disabled:opacity-38`}
              >
                <TypeIcon size={20} />
                <span
                  aria-hidden
                  className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
                />
              </button> */}
            </div>
          </div>
          {/* <button
            type="button"
            disabled
            className={`group text-on-surface-variant relative rounded-full p-2 transition duration-150 hover:cursor-pointer disabled:pointer-events-none disabled:opacity-38`}
          >
            <MicIcon size={20} />
            <span
              aria-hidden
              className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
            />
          </button> */}
        </form>
      </div>
    </div>
  );
}
